import { useCallback, useMemo, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@knowtex-ai/components";
import mcApi, { type McEvent, type McRun, type McRunDetail } from "../../lib/mc";
import { mcKeys } from "../../hooks/useMcRuns";
import { runFilePrefix, saveBlob, saveFindingsCsv } from "./download";
import { findingDetails } from "./runData";

/** useToast returns new functions each render; a ref keeps the callbacks below stable for table columns. */
function useStableToast() {
  const toast = useToast();
  const ref = useRef(toast);
  ref.current = toast;
  return ref;
}

/** Downloads a run's bundle (run.json, events, usage, findings and every artifact) as one zip. */
export function useDownloadRunBundle() {
  const toast = useStableToast();
  const { mutate, isPending, variables } = useMutation({
    mutationFn: async (runId: string) => {
      const { blob, filename } = await mcApi.getRunBundle(runId);
      saveBlob(blob, filename || `${runFilePrefix(runId)}.zip`);
    },
    onError: (error: Error & { status?: number }) =>
      toast.current.error(
        "Couldn't download the run",
        error.status === 404 ? "This MC API doesn't offer run downloads yet." : error.message
      ),
  });
  return useMemo(
    () => ({
      download: (runId: string) => {
        toast.current.info("Preparing the run download", "Runs with recordings and traces can take a minute.");
        mutate(runId);
      },
      isDownloading: (runId: string) => isPending && variables === runId,
    }),
    [isPending, variables, mutate, toast]
  );
}

/**
 * Downloads a run's findings as CSV. Pass the run's loaded artifacts and events when the page has them;
 * otherwise they're fetched so the CSV carries each finding's full detail.
 */
export function useDownloadFindingsCsv() {
  const toast = useStableToast();
  const queryClient = useQueryClient();
  return useCallback(
    async (run: McRun, loaded?: { run: McRunDetail; events: McEvent[] }) => {
      try {
        const [detail, events] = loaded
          ? [loaded.run, loaded.events]
          : await Promise.all([
              queryClient.fetchQuery({ queryKey: mcKeys.run(run.id), queryFn: () => mcApi.getRun(run.id) }),
              mcApi.getRunEventsAfter(run.id, 0),
            ]);
        saveFindingsCsv(run.id, findingDetails(detail.findings ?? [], events), detail.artifacts ?? []);
      } catch (error) {
        toast.current.error("Couldn't export the findings", (error as Error).message);
      }
    },
    [queryClient, toast]
  );
}
